<app-detail-panel
  [isOpen]="isOpen()"
  [resizable]="true"
  [(widthPx)]="widthPx"
  (panelClose)="close()"
>
  <!-- Header -->
  <div class="panel-header">
    <div class="panel-title-row">
      <app-section-icon class="header-icon" name="tasks" />
      <app-editable-panel-title
        [value]="task().name"
        [readonly]="!canWriteTasks()"
        ariaLabel="Task name"
        (commit)="onTitleCommit($event)"
      />
    </div>
    <div class="panel-header-actions">
      <app-status-indicator
        appearance="inline"
        [status]="getTaskStatusVariant(task().status)"
        [meaning]="getTaskStatusMeaning(task().status)"
        [label]="statusLabel()"
      />
      @if (isManager()) {
        <button
          class="panel-icon-btn panel-icon-btn--lg panel-icon-btn--danger"
          (click)="showDeleteConfirm.set(true)"
          aria-label="Delete task"
        >
          <mat-icon>delete</mat-icon>
        </button>
      }
      <button class="panel-icon-btn panel-icon-btn--lg" (click)="close()" aria-label="Close panel">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Sub-hero: taxonomy lede + property strip (canonical drawer
       identity zone, see `styles/detail-panel-shared.css`). The
       category label (Control / Manual / System) reads as the
       *kind* of task; due-date / recurrence / owner are properties
       about it. Status pill is in the header next to the title. -->
  <!-- No sub-hero. Status pill is top-right in header-actions (single
       state axis, same slot as document / control drawers). Category,
       due date, recurring, creator all render as first-class fields
       in the body's Details grid — sub-hero would have been pure
       duplication of body content. Sub-hero exists ONLY when a drawer
       has 2+ state axes (see assessment-info-panel). -->

  <!-- Tabs -->
  <app-panel-tabs
    [tabs]="panelTabs()"
    [activeTab]="activeTab()"
    (tabChange)="setTab($any($event))"
  />

  <!-- Tab body -->
  <div class="panel-body" role="tabpanel">
    <!-- ── OVERVIEW ── -->
    @if (activeTab() === 'overview') {
      <!-- Rejection / Approval banner -->
      @if (latestStatusComment(); as sc) {
        <div class="status-comment-card" [attr.data-type]="sc.comment_type.toLowerCase()">
          <div class="status-comment-header">
            <div class="status-comment-title">
              <mat-icon class="status-comment-icon">
                {{ sc.comment_type === 'REJECTION' ? 'cancel' : 'check_circle' }}
              </mat-icon>
              <span>{{ statusCommentTitle(sc) }}</span>
            </div>
            <div class="status-comment-header-right">
              @if (isEdited(sc)) {
                <span class="comment-edited">(edited)</span>
              }
              <span class="status-comment-date">{{ formatDateTime(sc.created_at) }}</span>
              <!-- Editing a rejection/approval note is a comment write, so
                   the capability is required on top of the existing
                   manager-only rule — `&&`, not a replacement, because a
                   status note is not something a delegate should reword. -->
              @if (isManager() && canWriteTasks() && editingCommentId() !== sc.comment_id) {
                <button
                  class="panel-icon-btn panel-icon-btn--lg"
                  (click)="startEditComment(sc)"
                  aria-label="Edit comment"
                >
                  <app-action-glyph name="edit-answers" />
                </button>
              }
            </div>
          </div>
          @if (editingCommentId() === sc.comment_id) {
            <textarea
              class="edit-input edit-textarea"
              rows="3"
              [value]="editingCommentContent()"
              (input)="editingCommentContent.set($any($event.target).value)"
            ></textarea>
            @if (commentError()) {
              <div
                role="alert"
                [class.denied-banner]="commentErrorIsDenial()"
                [class.edit-alert]="!commentErrorIsDenial()"
              >
                {{ commentError() }}
              </div>
            }
            <div class="status-comment-edit-actions">
              <button class="btn-secondary-sm" (click)="cancelCommentEdit()">Cancel</button>
              <button
                class="btn-primary-sm"
                [disabled]="savingComment() || !editingCommentContent().trim()"
                (click)="saveCommentEdit(sc.comment_id)"
              >
                {{ savingComment() ? 'Saving…' : 'Save' }}
              </button>
            </div>
          } @else {
            <p
              class="status-comment-text"
              [class.status-comment-text--collapsed]="!statusCommentExpanded()"
            >
              {{ sc.content }}
            </p>
            @if (sc.content.length > 160) {
              <button
                class="status-comment-toggle"
                (click)="statusCommentExpanded.set(!statusCommentExpanded())"
              >
                @if (statusCommentExpanded()) {
                  <mat-icon>expand_less</mat-icon> Show less
                } @else {
                  <mat-icon>expand_more</mat-icon> Show more
                }
              </button>
            }
          }
        </div>
      }

      <!-- Actions -->
      <div class="panel-section">
        <div class="panel-section-label">Actions</div>
        <p class="panel-empty-text">{{ createActionLabel() ?? taskTypeLabel() }}</p>
      </div>

      <!-- Description -->
      <div class="panel-section">
        <div class="panel-section-label">Description</div>
        <app-inline-text
          [multiline]="true"
          [value]="task().description ?? ''"
          [readonly]="!canWriteTasks()"
          [placeholder]="
            canWriteTasks() ? 'Describe what needs to be done…' : 'No description provided.'
          "
          ariaLabel="Task description"
          (commit)="onDescriptionCommit($event)"
        />
      </div>

      <!-- Details -->
      <div class="panel-section">
        <div class="panel-section-label">Details</div>
        <div class="panel-details-grid">
          <!-- Category (task kind) — moved here from the sub-hero so
               every drawer's identity strip stays state-only or empty.
               `category()` returns the display string (Control /
               Manual / System / …). -->
          <div class="panel-detail-item">
            <div class="panel-detail-label">Category</div>
            <div class="panel-detail-value">{{ category() }}</div>
          </div>
          <div class="panel-detail-item">
            <div class="panel-detail-label">Start date</div>
            <div class="panel-detail-value">
              @if (task().recurring || !canManageCompliance()) {
                {{ task().start_date ? formatDate(task().start_date) : '—' }}
              } @else {
                <div class="inline-date-display-row">
                  <span>{{ task().start_date ? formatDate(task().start_date) : '—' }}</span>
                  <button
                    class="panel-icon-btn"
                    aria-label="Change start date"
                    (click)="startDateInput.showPicker()"
                  >
                    <mat-icon>edit_calendar</mat-icon>
                  </button>
                  <input
                    #startDateInput
                    type="date"
                    class="inline-date-hidden"
                    [value]="task().start_date ?? ''"
                    [attr.max]="task().due_date || null"
                    aria-label="Start date"
                    (change)="onStartDateChange(startDateInput.value)"
                  />
                </div>
              }
            </div>
          </div>
          <div class="panel-detail-item">
            <div class="panel-detail-label">Due date</div>
            <div class="panel-detail-value">
              @if (task().recurring || !canManageCompliance()) {
                {{ task().due_date ? formatDate(task().due_date) : '—' }}
              } @else {
                <div class="inline-date-display-row">
                  <span>{{ task().due_date ? formatDate(task().due_date) : '—' }}</span>
                  <button
                    class="panel-icon-btn"
                    aria-label="Change due date"
                    (click)="dueDateInput.showPicker()"
                  >
                    <mat-icon>edit_calendar</mat-icon>
                  </button>
                  <input
                    #dueDateInput
                    type="date"
                    class="inline-date-hidden"
                    [value]="task().due_date ?? ''"
                    [attr.min]="task().start_date || null"
                    aria-label="Due date"
                    (change)="onDueDateChange(dueDateInput.value)"
                  />
                </div>
              }
            </div>
          </div>
          <div class="panel-detail-item">
            <div class="panel-detail-label">Needs approval</div>
            <div class="panel-detail-value">
              @if (!canWriteTasks()) {
                {{ task().needs_approval ? 'Yes' : 'No' }}
              } @else {
                <app-select
                  variant="ghost"
                  label="Needs approval"
                  [options]="needsApprovalOptions()"
                  [value]="task().needs_approval ? 'yes' : 'no'"
                  [clearable]="false"
                  (valueChange)="onNeedsApprovalChange($event)"
                />
              }
            </div>
          </div>
          <div class="panel-detail-item">
            <div class="panel-detail-label">Recurring</div>
            <div class="panel-detail-value">
              {{ task().recurring ? 'Yes' : 'No' }}
            </div>
          </div>
          <div class="panel-detail-item">
            <div class="panel-detail-label">Created by</div>
            <div class="panel-detail-value">{{ task().created_by_name }}</div>
          </div>
          <div class="panel-detail-item">
            <div class="panel-detail-label">{{ 'panels_ext.field_assignees' | transloco }}</div>
            <div class="panel-detail-value">
              <app-assignee-picker
                [assigneeIds]="task().assignees ?? []"
                [members]="orgMembers()"
                [readonly]="assigneeReadonly()"
                (assigneesChange)="onAssigneesChange($event)"
              />
            </div>
          </div>
          @if (task().language) {
            <div class="panel-detail-item">
              <div class="panel-detail-label">{{ 'language_picker.label' | transloco }}</div>
              <div class="panel-detail-value">
                <app-language-badge [language]="task().language" appearance="inline" />
              </div>
            </div>
          }
        </div>
        @if (task().recurring) {
          <p class="edit-hint">
            {{ 'panels_ext.due_date_follows_control_period' | transloco }}
          </p>
        }
      </div>

      <!-- Source -->
      <div class="panel-section">
        <div class="panel-section-label">Source</div>
        @if (sources().length > 0) {
          @for (source of sources(); track source.id) {
            @if (source.kind) {
              <div
                class="source-card source-card-clickable"
                role="button"
                tabindex="0"
                [attr.aria-label]="'Open linked ' + source.label.toLowerCase()"
                (click)="openLinkedEntity(source.kind, source.id)"
                (keydown.enter)="openLinkedEntity(source.kind, source.id)"
                (keydown.space)="$event.preventDefault(); openLinkedEntity(source.kind, source.id)"
              >
                <mat-icon class="source-icon">{{ source.icon }}</mat-icon>
                <div class="source-body">
                  <span class="source-name" [title]="source.name">{{ source.name }}</span>
                  <span class="source-subtitle" [title]="source.detail ?? ''">{{
                    source.detail ?? source.label + '-linked task'
                  }}</span>
                </div>
                <app-badge [text]="source.label" />
                <mat-icon class="source-open-icon" aria-hidden="true">arrow_forward</mat-icon>
              </div>
            } @else {
              <div class="source-card">
                <mat-icon class="source-icon">{{ source.icon }}</mat-icon>
                <div class="source-body">
                  <span class="source-name" [title]="source.name">{{ source.name }}</span>
                  <span class="source-subtitle">{{ source.label }}-linked task</span>
                </div>
                <app-badge [text]="source.label" />
              </div>
            }
          }
        } @else if (task().created_by_name === 'System') {
          <div class="source-card">
            <mat-icon class="source-icon">star_border</mat-icon>
            <div class="source-body">
              <span class="source-name">System</span>
              <span class="source-subtitle">System-generated task</span>
            </div>
            <app-badge text="System" />
          </div>
        } @else {
          <div class="source-card">
            <mat-icon class="source-icon">star_border</mat-icon>
            <div class="source-body">
              <span class="source-name">General</span>
              <span class="source-subtitle">Standalone task</span>
            </div>
            <app-badge text="General" />
          </div>
        }
      </div>

      @if (isManager() && task().status === 'WAITING_FOR_APPROVAL') {
        <div class="panel-section panel-review-actions">
          <div class="panel-section-label">Review</div>
          <div class="review-action-row">
            <button class="review-btn review-btn--reject" (click)="openActionModal('reject')">
              <mat-icon>cancel</mat-icon>
              Reject
            </button>
            <button class="review-btn review-btn--approve" (click)="openActionModal('approve')">
              <mat-icon>check_circle</mat-icon>
              Approve
            </button>
          </div>
        </div>
      }
    }

    <!-- ── EVIDENCE ── -->
    @if (activeTab() === 'evidence') {
      @if (evidenceError()) {
        <!-- Above the drop zone, not inside it: the zone is a label wrapping
             a file input, so a banner in there would be part of the click
             target. Amber lock for a refusal, red for a genuine failure —
             the same split the task forms use. -->
        <div class="panel-section">
          <div
            role="alert"
            [class.denied-banner]="evidenceErrorIsDenial()"
            [class.edit-alert]="!evidenceErrorIsDenial()"
          >
            {{ evidenceError() }}
          </div>
        </div>
      }

      @if (task().status === 'CREATED' || task().status === 'REJECTED') {
        <div class="panel-section">
          <div class="panel-section-label">Upload New Evidence</div>
          <!-- Kept visible and dimmed rather than hidden, same as the add-task
               button: hiding it tells a view-only delegate nothing, where a
               dead control plus its reason says what a fuller grant would
               give them. `matTooltip` sits directly on the label — the
               wrapper-span idiom is only needed for *disabled buttons*, whose
               `pointer-events: none` swallows the hover. A label has no
               disabled state, so it still reports hovers while `for` is
               dropped and the handlers refuse. -->
          <label
            class="task-upload-zone"
            [class.dragging]="isDragging()"
            [class.uploading]="uploading()"
            [class.is-disabled]="!canWriteEvidence()"
            [attr.for]="canWriteEvidence() ? 'task-evidence-input' : null"
            [matTooltip]="evidenceDisabledReason()"
            matTooltipPosition="above"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave()"
            (drop)="onDrop($event)"
          >
            @if (uploading()) {
              <div class="loading-overlay">
                <div class="loading-spinner">
                  <mat-icon class="spinner-icon">hourglass_empty</mat-icon>
                  <span>Uploading...</span>
                </div>
              </div>
            }
            <mat-icon class="task-upload-icon">upload</mat-icon>
            <span class="task-upload-primary">Drop files here or click to select</span>
            <span class="task-upload-sub">PDF, Images, Videos, DOCX up to 500MB</span>
            <input
              id="task-evidence-input"
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.docx,.doc,.mp4,.mov,.webm,.avi,.mkv,.m4v"
              [disabled]="uploading() || !canWriteEvidence()"
              (change)="onFileSelected($event)"
              style="display: none"
            />
          </label>
        </div>
      }

      @if (loadingEvidence()) {
        <div class="panel-section">
          <div class="panel-muted-text">Loading evidence…</div>
        </div>
      } @else if (evidenceFiles().length > 0) {
        <div class="panel-section">
          <div class="panel-section-label">Uploaded Evidence</div>
          <div class="task-evidence-list">
            @for (file of evidenceFiles(); track file.evidence_id) {
              <div class="task-evidence-item">
                <span [class]="'task-file-type task-file-type-' + getFileType(file).toLowerCase()">
                  {{ getFileType(file) }}
                </span>
                <div class="task-evidence-info">
                  <div class="task-evidence-name">{{ file.file_name ?? file.evidence_id }}</div>
                  <div class="task-evidence-meta">
                    Uploaded by {{ file.uploaded_by_name ?? file.uploaded_by }} ·
                    {{ formatDate(file.created_at) }}
                  </div>
                </div>
                <button
                  class="task-evidence-download"
                  (click)="openFile(file)"
                  [disabled]="!file.download_url"
                  [attr.aria-label]="'Open ' + file.file_name"
                >
                  <mat-icon>open_in_new</mat-icon>
                </button>
                <!-- Hidden rather than dimmed, unlike the upload zone: this is
                     a bare icon button with no label, so a disabled one
                     communicates nothing. The zone above is where the reason
                     is stated, and it stays visible to state it. -->
                @if (
                  canWriteEvidence() &&
                  (task().status === 'CREATED' || task().status === 'REJECTED')
                ) {
                  <button
                    class="task-evidence-delete"
                    (click)="evidenceToDelete.set(file.evidence_id)"
                    [attr.aria-label]="'Delete ' + file.file_name"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                }
              </div>
            }
          </div>
        </div>
      }
    }

    <!-- ── COMMENTS ── -->
    @if (activeTab() === 'comments') {
      <div class="comments-body">
        @if (!addCommentExpanded()) {
          <!-- Collapsed: input + sort + refresh in one row -->
          <div class="comments-toolbar">
            <!-- Visible and dimmed, not hidden: the thread is still worth
                 reading on a view-only grant, and a dead composer with its
                 reason says what a fuller grant would add. Wrapped in a span
                 because a disabled button gets `pointer-events: none` and
                 would never report the hover. -->
            <span
              class="add-comment-collapsed-wrap"
              [matTooltip]="commentDisabledReason()"
              matTooltipPosition="above"
            >
              <button
                class="add-comment-collapsed"
                [disabled]="!canWriteTasks()"
                (click)="openAddComment()"
              >
                Write a comment...
              </button>
            </span>
            <div class="comments-toolbar-actions">
              <button
                class="comments-order-btn"
                (click)="toggleCommentsOrder()"
                [disabled]="commentsLoading()"
                [title]="commentsOrder() === 'DESC' ? 'Newest first' : 'Oldest first'"
              >
                <mat-icon>{{
                  commentsOrder() === 'ASC' ? 'arrow_upward' : 'arrow_downward'
                }}</mat-icon>
              </button>
              <app-refresh-button [loading]="commentsLoading()" (refreshClick)="loadComments()" />
            </div>
          </div>
        } @else {
          <!-- Expanded: full textarea replaces toolbar -->
          <div class="add-comment-form">
            <textarea
              #newCommentInput
              class="add-comment-input"
              [value]="newCommentContent()"
              (input)="newCommentContent.set($any($event.target).value)"
              placeholder="Write a comment..."
              rows="3"
            ></textarea>
            <div class="add-comment-footer">
              <button class="comment-btn comment-btn--secondary" (click)="cancelAddComment()">
                Cancel
              </button>
              <button
                class="comment-btn comment-btn--primary"
                [disabled]="addingComment() || !newCommentContent().trim()"
                (click)="addComment()"
              >
                @if (addingComment()) {
                  Posting...
                } @else {
                  Post
                }
              </button>
            </div>
          </div>
        }

        @if (commentError()) {
          <div
            role="alert"
            [class.denied-banner]="commentErrorIsDenial()"
            [class.edit-alert]="!commentErrorIsDenial()"
          >
            {{ commentError() }}
          </div>
        }

        @if (commentsLoading()) {
          <p class="panel-empty-text">Loading comments...</p>
        } @else if (commentsError() && !noteComments().length) {
          <p class="panel-empty-text">Failed to load comments.</p>
        } @else {
          <div class="comment-list">
            @for (comment of noteComments(); track comment.comment_id) {
              <div class="comment-item">
                <div
                  class="comment-avatar"
                  [style.background]="
                    canModifyComment(comment) ? 'var(--primary-color)' : '#6b7280'
                  "
                >
                  {{ getInitials(comment.created_by_name) }}
                </div>
                <div class="comment-content-wrap">
                  <div class="comment-header">
                    <div class="comment-meta">
                      <span class="comment-author">{{ comment.created_by_name }}</span>
                      <span class="comment-date">{{ formatDateTime(comment.created_at) }}</span>
                      @if (isEdited(comment)) {
                        <span class="comment-edited">(edited)</span>
                      }
                    </div>
                    @if (
                      canWriteTasks() &&
                      canModifyComment(comment) &&
                      editingCommentId() !== comment.comment_id
                    ) {
                      <div class="comment-actions">
                        <button
                          class="comment-action-btn"
                          (click)="startEditComment(comment)"
                          aria-label="Edit comment"
                        >
                          <app-action-glyph name="edit-answers" />
                        </button>
                        <button
                          class="comment-action-btn comment-action-btn--danger"
                          (click)="deleteComment(comment.comment_id)"
                          aria-label="Delete comment"
                        >
                          <mat-icon>delete</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                  @if (editingCommentId() === comment.comment_id) {
                    <textarea
                      class="comment-edit-input"
                      [value]="editingCommentContent()"
                      (input)="editingCommentContent.set($any($event.target).value)"
                      rows="3"
                    ></textarea>
                    <div class="comment-edit-actions">
                      <button
                        class="comment-btn comment-btn--secondary"
                        (click)="cancelCommentEdit()"
                      >
                        Cancel
                      </button>
                      <button
                        class="comment-btn comment-btn--primary"
                        [disabled]="savingComment()"
                        (click)="saveCommentEdit(comment.comment_id)"
                      >
                        @if (savingComment()) {
                          Saving...
                        } @else {
                          Save
                        }
                      </button>
                    </div>
                  } @else {
                    <p class="comment-text">{{ comment.content }}</p>
                  }
                </div>
              </div>
            } @empty {
              <p class="panel-empty-text">No comments yet.</p>
            }
          </div>
        }
      </div>
    }

    <!-- ── HISTORY ── -->
    @if (activeTab() === 'history') {
      <app-audit-timeline
        [events]="auditLoader.auditTrail()"
        [loading]="auditLoader.loadingAuditTrail()"
        [loadFailed]="auditLoader.loadError() !== null"
        entityLabel="this task"
        (refresh)="auditLoader.load()"
      />
    }
  </div>

  @if (task().status === 'CREATED' || task().status === 'REJECTED') {
    <div class="panel-action-footer">
      @if (completeError()) {
        <div class="edit-alert">{{ completeError() }}</div>
      }
      @if (showCompletedAtPicker()) {
        <div class="backfill-date-row">
          <label class="backfill-date-label" for="backfill-completed-at">Completion date</label>
          <input
            #backfillDateInput
            id="backfill-completed-at"
            type="date"
            class="inline-date"
            [value]="backfilledCompletedAt()"
            [attr.min]="task().start_date || null"
            [attr.max]="task().due_date || todayDate"
            aria-label="Completion date"
            (change)="backfilledCompletedAt.set(backfillDateInput.value)"
          />
        </div>
      }
      <span [matTooltip]="canCompleteTooltip()" matTooltipPosition="above">
        <button
          class="task-complete-btn"
          [disabled]="isCompleting() || uploading() || !canComplete()"
          (click)="showCompleteConfirm.set(true)"
        >
          @if (isCompleting()) {
            Submitting…
          } @else if (task().status === 'REJECTED') {
            Submit for Review
          } @else if (task().needs_approval) {
            Submit for Approval
          } @else {
            Complete Task
          }
        </button>
      </span>
    </div>
  }
</app-detail-panel>

<app-modal
  [isVisible]="pendingAction() === 'approve'"
  [config]="approveModalConfig()"
  (confirmed)="submitAction()"
  (cancelled)="cancelAction()"
  (secondary)="cancelAction()"
  (closed)="cancelAction()"
>
  <div class="action-modal-body">
    @if (actionError()) {
      <div class="edit-alert">{{ actionError() }}</div>
    }
    <label class="edit-label" for="approve-comment"
      >Comment <span class="action-modal-optional">(optional)</span></label
    >
    <textarea
      id="approve-comment"
      class="action-modal-textarea"
      [value]="actionComment()"
      (input)="actionComment.set($any($event.target).value)"
      placeholder="Add a comment..."
      rows="4"
    ></textarea>
  </div>
</app-modal>

<app-modal
  [isVisible]="pendingAction() === 'reject'"
  [config]="rejectModalConfig()"
  (confirmed)="submitAction()"
  (cancelled)="cancelAction()"
  (secondary)="cancelAction()"
  (closed)="cancelAction()"
>
  <div class="action-modal-body">
    @if (actionError()) {
      <div class="edit-alert">{{ actionError() }}</div>
    }
    <label class="edit-label" for="reject-comment"
      >Reason <span class="action-modal-required">*</span></label
    >
    <textarea
      id="reject-comment"
      class="action-modal-textarea"
      [value]="actionComment()"
      (input)="actionComment.set($any($event.target).value)"
      placeholder="Explain why this task is being rejected..."
      rows="4"
    ></textarea>
  </div>
</app-modal>

<app-confirm-modal
  [isVisible]="!!evidenceToDelete()"
  title="Delete evidence"
  message="Are you sure you want to delete this file? This cannot be undone."
  confirmText="Delete"
  cancelText="Cancel"
  [isLoading]="isDeletingEvidence()"
  (confirmed)="confirmDeleteEvidence()"
  (cancelled)="evidenceToDelete.set(null)"
  (modalClosed)="evidenceToDelete.set(null)"
/>

<app-confirm-modal
  [isVisible]="!!commentToDelete()"
  title="Delete comment"
  message="Are you sure you want to delete this comment? This cannot be undone."
  confirmText="Delete"
  cancelText="Cancel"
  (confirmed)="confirmDeleteComment()"
  (cancelled)="commentToDelete.set(null)"
  (modalClosed)="commentToDelete.set(null)"
/>

<app-confirm-modal
  [isVisible]="showDeleteConfirm()"
  title="Delete task"
  [message]="'Are you sure you want to delete \'' + task().name + '\'? This cannot be undone.'"
  confirmText="Delete"
  cancelText="Cancel"
  [isLoading]="isDeleting()"
  [errorMessage]="deleteError()"
  (confirmed)="confirmDelete()"
  (cancelled)="showDeleteConfirm.set(false)"
  (modalClosed)="showDeleteConfirm.set(false)"
/>

<app-confirm-modal
  [isVisible]="showCompleteConfirm()"
  title="Confirm action"
  [message]="completeConfirmMessage()"
  [confirmText]="completeConfirmText()"
  cancelText="Cancel"
  [isLoading]="isCompleting()"
  (confirmed)="completeTask()"
  (cancelled)="showCompleteConfirm.set(false)"
  (modalClosed)="showCompleteConfirm.set(false)"
/>
